import Service, { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import type { UploadFile } from 'ember-file-upload';
import type PersonModel from 'tio-data/models/person';
import type ObservableDocumentModel from 'tio-data/models/observable-document';
import type DigitalAssetsService from './digital-assets';

type SerializableValue =
  | string
  | number
  | boolean
  | null
  | SerializableValue[]
  | SerializableRecord;
type SerializableRecord = { [key: string]: SerializableValue };
type ObservableDocumentPayload = {
  data: [
    {
      type: 'observable-document';
      id: string;
      attributes: SerializableRecord;
    },
  ];
};

export type ObservableDocumentAttributes = SerializableRecord & { id: string };
export default class ObservablesService extends Service {
  @service declare store: StoreService;
  @service declare digitalAssets: DigitalAssetsService;

  createObservableDocument = async (
    uploadFile: UploadFile,
    options: { person: PersonModel; batch?: string; via?: string }
  ): Promise<ObservableDocumentModel> => {
    const { person, batch, via = 'UPLOAD' } = options;

    // get API path for observable assets
    const collectionPath = this.store
      .adapterFor('application')
      .urlForCreateRecord('observable-asset');

    // set headers
    const headers = this.store.adapterFor('application')?.headers;
    headers['Content-Type'] = 'application/vnd.api+json';

    // create asset record through API
    const assetResponse = await fetch(`${collectionPath}/s3-signed-url`, {
      headers: headers,
      method: 'POST',
      body: JSON.stringify({ filename: uploadFile.file.name, public_image: false }),
    });

    // read asset response content and upload binary to signed url
    const assetDetails = await assetResponse.json();

    // set asset detail classification if created via proxy; backend will derive this
    // automatically for txt or pdf files, but admin users creating an observable
    // document by proxy should be able to provide an image file when appropriate
    if (via === 'PROXY') assetDetails.classification = 'ObservableProvider.PROXY';
    await uploadFile.uploadBinary(assetDetails.url, { method: 'PUT' });

    // create and save observable asset
    const observableAsset = this.digitalAssets.createAssetFromS3Details(
      'observable-asset',
      assetDetails
    );
    await observableAsset.save();

    const contentType = observableAsset.contentType;
    const provider = observableAsset.classification;

    // create and save observable document
    const observableDocument = this.store.createRecord('observable-document', {
      observableAsset,
      person,
      batch,
      contentType,
      provider,
      via,
    }) as ObservableDocumentModel;
    await observableDocument.save();

    return observableDocument;
  };

  updateObservableDocument = (
    attributes: ObservableDocumentAttributes
  ): ObservableDocumentModel => {
    const { id, ...attributeValues } = attributes;

    const payload: ObservableDocumentPayload = {
      data: [
        {
          type: 'observable-document',
          id,
          attributes: attributeValues as SerializableRecord,
        },
      ],
    };

    const pushed = this.store.push(payload as Parameters<StoreService['push']>[0]);

    if (!pushed) {
      throw new Error('Failed to update observable document.');
    }

    const [observableDocument] = pushed as ObservableDocumentModel[];

    if (!observableDocument) {
      throw new Error('Observable document missing from store response.');
    }

    return observableDocument;
  };
}
